/**
 * ============================================================
 * FROUGE — Header Interaction System
 * File: assets/frouge-header.js
 * Version: 1.0 — Self Care Couture
 *
 * PASTE THIS FILE INTO:
 * Shopify Admin → Online Store → Themes → Edit Code
 * → assets/ → Add new asset → frouge-header.js
 *
 * THEN ADD THIS LINE to layout/theme.liquid
 * just before the closing </body> tag:
 * <script src="{{ 'frouge-header.js' | asset_url }}" defer></script>
 * ============================================================
 */

(function () {
  'use strict';

  // ── DOM References ──────────────────────────────────────
  const header      = document.getElementById('frouge-header');
  const menuBtn     = document.getElementById('frouge-menu-btn');
  const menuClose   = document.getElementById('frouge-menu-close');
  const mobileMenu  = document.getElementById('frouge-mobile-menu');
  const searchBtn   = document.getElementById('frouge-search-btn');
  const searchClose = document.getElementById('frouge-search-close');
  const searchDrawer = document.getElementById('frouge-search-drawer');
  const searchInput = document.getElementById('frouge-search-input');
  const scrim       = document.getElementById('frouge-scrim');

  // ── Scroll State ────────────────────────────────────────
  // Adds terracotta border-bottom when page has scrolled
  let ticking = false;
  const SCROLL_THRESHOLD = 10;

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        if (header) {
          if (window.scrollY > SCROLL_THRESHOLD) {
            header.classList.add('is-scrolled');
          } else {
            header.classList.remove('is-scrolled');
          }
        }
        ticking = false;
      });
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  // ── Mobile Menu ──────────────────────────────────────────
  function openMobileMenu() {
    if (!mobileMenu || !menuBtn) return;
    mobileMenu.setAttribute('aria-hidden', 'false');
    menuBtn.setAttribute('aria-expanded', 'true');
    document.body.classList.add('frouge-menu-open');
    scrim && activateScrim();
    // Focus first link in menu for accessibility
    const firstLink = mobileMenu.querySelector('a, button');
    if (firstLink) setTimeout(() => firstLink.focus(), 50);
  }

  function closeMobileMenu() {
    if (!mobileMenu || !menuBtn) return;
    mobileMenu.setAttribute('aria-hidden', 'true');
    menuBtn.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('frouge-menu-open');
    deactivateScrim();
    menuBtn.focus();
  }

  menuBtn   && menuBtn.addEventListener('click', openMobileMenu);
  menuClose && menuClose.addEventListener('click', closeMobileMenu);

  // Mobile sub-menus
  const mobileParentBtns = document.querySelectorAll('.frouge-mobile-menu__link--parent');
  mobileParentBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      const sub = btn.nextElementSibling;

      // Close all others first
      mobileParentBtns.forEach(other => {
        if (other !== btn) {
          other.setAttribute('aria-expanded', 'false');
          const otherSub = other.nextElementSibling;
          if (otherSub) otherSub.setAttribute('aria-hidden', 'true');
        }
      });

      btn.setAttribute('aria-expanded', String(!expanded));
      if (sub) sub.setAttribute('aria-hidden', String(expanded));
    });
  });

  // ── Search Drawer ────────────────────────────────────────
  function openSearch() {
    if (!searchDrawer || !searchBtn) return;
    searchDrawer.setAttribute('aria-hidden', 'false');
    searchBtn.setAttribute('aria-expanded', 'true');
    scrim && activateScrim();
    if (searchInput) setTimeout(() => searchInput.focus(), 50);
  }

  function closeSearch() {
    if (!searchDrawer || !searchBtn) return;
    searchDrawer.setAttribute('aria-hidden', 'true');
    searchBtn.setAttribute('aria-expanded', 'false');
    deactivateScrim();
    searchBtn.focus();
  }

  searchBtn   && searchBtn.addEventListener('click', openSearch);
  searchClose && searchClose.addEventListener('click', closeSearch);

  // ── Scrim ─────────────────────────────────────────────────
  function activateScrim() {
    if (!scrim) return;
    scrim.classList.add('is-active');
    scrim.setAttribute('aria-hidden', 'false');
  }

  function deactivateScrim() {
    if (!scrim) return;
    scrim.classList.remove('is-active');
    scrim.setAttribute('aria-hidden', 'true');
  }

  // Click scrim to close all drawers
  scrim && scrim.addEventListener('click', () => {
    closeMobileMenu();
    closeSearch();
    closeAllDropdowns();
  });

  // ── Desktop Dropdowns ─────────────────────────────────────
  const desktopParentBtns = document.querySelectorAll('.frouge-header__nav-link--parent');

  function closeAllDropdowns() {
    desktopParentBtns.forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
      const dropdown = btn.nextElementSibling;
      if (dropdown) dropdown.setAttribute('aria-hidden', 'true');
    });
  }

  desktopParentBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      const dropdown = btn.nextElementSibling;

      closeAllDropdowns();

      if (!expanded) {
        btn.setAttribute('aria-expanded', 'true');
        if (dropdown) dropdown.setAttribute('aria-hidden', 'false');
      }
    });
  });

  // Close dropdowns when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.frouge-header__nav-item')) {
      closeAllDropdowns();
    }
  });

  // ── Keyboard Navigation ───────────────────────────────────
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Close mobile menu
      if (mobileMenu && mobileMenu.getAttribute('aria-hidden') === 'false') {
        closeMobileMenu();
        return;
      }
      // Close search
      if (searchDrawer && searchDrawer.getAttribute('aria-hidden') === 'false') {
        closeSearch();
        return;
      }
      // Close dropdowns
      closeAllDropdowns();
    }
  });

  // Trap focus in mobile menu when open
  mobileMenu && mobileMenu.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusable = mobileMenu.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // ── Cart Count Update (Shopify AJAX) ─────────────────────
  // Listen for Shopify cart updates and refresh count
  document.addEventListener('cart:update', () => {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const countEl = document.querySelector('.frouge-header__cart-count');
        if (cart.item_count > 0) {
          if (countEl) {
            countEl.textContent = cart.item_count;
          } else {
            const cartBtn = document.querySelector('.frouge-header__cart-btn');
            if (cartBtn) {
              const span = document.createElement('span');
              span.className = 'frouge-header__cart-count';
              span.setAttribute('aria-hidden', 'true');
              span.textContent = cart.item_count;
              cartBtn.appendChild(span);
            }
          }
        } else if (countEl) {
          countEl.remove();
        }
      })
      .catch(() => {}); // Fail silently — cart still functions
  });

  // ── Announce Bar Auto-pause on Reduced Motion ─────────────
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const track = document.querySelector('.frouge-announce__track');
    if (track) track.style.animation = 'none';
  }

})();
